import React, { useEffect, useState, useCallback } from 'react';
import api from '@/utils/api';
import { Search, Package } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';

export default function FulfillmentCatalog() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadCatalog = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/tenant/fulfillment-catalog', { params: { search: search || undefined } });
      setProducts(res.data);
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error('Not on a fulfillment subscription');
      } else {
        toast.error('Failed to load catalog');
      }
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(loadCatalog, 300);
    return () => clearTimeout(timer);
  }, [loadCatalog]);

  // Read-only effective Price shown in the table:
  //   tenant_price (if set) → MAP → null
  const getEffectivePrice = (product) => {
    if (product.tenant_price !== null && product.tenant_price !== undefined) return product.tenant_price;
    if (product.min_advertised_price !== null && product.min_advertised_price !== undefined) return product.min_advertised_price;
    return null;
  };

  const formatMoney = (v) =>
    v === null || v === undefined || isNaN(v) ? '—' : `$${Number(v).toFixed(2)}`;
  // "Type" column: category is always populated on every product, so it's
  // used as the base label. `subcategory` is only ever set on Custom Shaft
  // products (it records which club type the shaft fits) - shown as a
  // suffix for shafts only, never as the sole label for club products.
  const CATEGORY_LABELS = {
    drivers: 'Driver',
    fairways: 'Fairway Wood',
    hybrids: 'Hybrid',
    irons: 'Iron',
    putters: 'Putter',
    wedges: 'Wedge',
    shafts: 'Custom Shaft',
  };
  const SHAFT_FIT_LABELS = { wood: 'Driver/Wood', iron: 'Iron', hybrid: 'Hybrid', wedge: 'Wedge', putter: 'Putter' };

  const typeLabel = (product) => {
    const base = CATEGORY_LABELS[product.category] || product.category || '—';
    if (product.category === 'shafts' && product.subcategory) {
      return `${base} • ${SHAFT_FIT_LABELS[product.subcategory] || product.subcategory}`;
    }
    return base;
  };

  if (loading && products.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#334155] border-r-transparent" />
        <p className="mt-4 text-gray-600">Loading fulfillment catalog...</p>
      </div>
    );
  }

  return (
    <div data-testid="fulfillment-catalog">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Fulfillment Catalog Pricing
        </h2>
        <span className="text-sm text-gray-500" data-testid="catalog-count">{products.length} products</span>
      </div>

      <p className="text-sm text-gray-600 mb-6">
        Review your retail prices for the ImpactGolf master catalog. "Your Cut" is your
        estimated 50% profit share at that price, after wholesale cost, processing, and
        platform fees — the same formula used for your real payouts.
      </p>

      {/* Controls row: search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-gray-50 border-[#E8E4DC]"
            data-testid="catalog-search"
          />
        </div>
      </div>

      {/* Product Table */}
      <div className="overflow-x-auto border border-[#E8E4DC] rounded-lg">
        <table className="w-full text-sm" data-testid="catalog-table">
          <thead className="bg-gray-50 border-b border-[#E8E4DC]">
            <tr>
              <th className="text-left px-4 py-3 font-bold text-gray-700">Product</th>
              <th className="text-left px-4 py-3 font-bold text-gray-700">Type</th>
              <th className="text-right px-4 py-3 font-bold text-gray-700">Price</th>
              <th className="text-right px-4 py-3 font-bold text-gray-700">Your Cut</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F3F4F6]">
            {products.map((product) => {
              const price = getEffectivePrice(product);

              return (
                <tr key={product.product_id} className="hover:bg-gray-50/50" data-testid={`catalog-row-${product.product_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {product.image_url && (
                        <img
                          src={product.image_url.startsWith('http') ? product.image_url : `${product.image_url}`}
                          alt=""
                          className="h-10 w-10 rounded object-cover bg-gray-100"
                        />
                      )}
                      <div>
                        <p className="font-medium text-gray-900 line-clamp-1">{product.name}</p>
                        <p className="text-xs text-gray-500">{product.manufacturer}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    <span className="text-xs bg-gray-100 px-2 py-0.5 rounded font-medium">
                      {typeLabel(product)}
                    </span>
                  </td>
                  <td
                    className="px-4 py-3 text-right text-gray-900 font-medium tabular-nums"
                    data-testid={`price-${product.product_id}`}
                  >
                    {formatMoney(price)}
                  </td>
                  <td
                    className="px-4 py-3 text-right text-emerald-700 font-bold tabular-nums"
                    data-testid={`tenant-cut-${product.product_id}`}
                  >
                    {formatMoney(product.tenant_cut)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {products.length === 0 && !loading && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-[#E8E4DC] mt-4">
          <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No products found in the fulfillment catalog.</p>
        </div>
      )}
    </div>
  );
}
